import { Box, Button, Title, TextField } from 'vopay';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useState, useEffect } from 'react';
import { Formik } from 'formik';
import type { ChangePasswordHandler } from '../types';
import PasswordValidationWidget from '../../UI/PasswordValidationWidget';
import styles from './ChangeUserPasswordForm.module.scss';
import { UtilButton } from 'UI';
import { useHistory } from 'react-router-dom';
import usePostPortalClientLogin from '@/hooks/useApi/usePostPortalClientLogin';
import usePasswordDialogue from '@/hooks/useDialogues/usePartnerLoginPassword.dialogue';

type ChangeUserPasswordProps = {
  handlers: ChangePasswordHandler;
  email: string;
};
type FC = FunctionComponent<ChangeUserPasswordProps>;

const ChangeUserPassword: FC = ({ handlers, email }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.ChangeUserPassword.ChangeUserPasswordForm' });
  const { t: tUsersList } = useTranslation('translations', { keyPrefix: 'Users.UsersList' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();
  const [currentPasswordResponse, postCurrentPassword] = usePostPortalClientLogin({});
  const [hasValidPassword, setHasValidPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [passwordError, setPasswordErorr] = useState(false);
  const { showDialogue: showCurrentPasswordDialogue } = usePasswordDialogue(currentPasswordResponse);
  const [valueSet, setValueSet] = useState({
    EmailAddress: '',
    NewPassword: '',
    SendEmail: true,
  });

  useEffect(() => {
    if (currentPasswordResponse?.ErrorMessage) showCurrentPasswordDialogue();
    if (currentPasswordResponse?.Success) handlers.onChangeUserPassword(valueSet);
  }, [currentPasswordResponse]);

  return (
    <Formik
      initialValues={{
        currentPassword: '',
        emailAddress: '',
        newPassword: '',
        shouldSendWelcomeEmail: true,
      }}
      onSubmit={() => {
        handlers.onChangeUserPassword(valueSet);
      }}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, isValid }) => {
        const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (currentPassword === newPassword) {
            setPasswordErorr(true);
          } else {
            setPasswordErorr(false);
            postCurrentPassword({ Username: email, Password: currentPassword });
          }
        };

        useEffect(() => {
          setValueSet({
            EmailAddress: values.emailAddress,
            NewPassword: values.newPassword,
            SendEmail: values.shouldSendWelcomeEmail,
          });
        }, [values]);

        return (
          <form onSubmit={onSubmit}>
            <Box flx="col" mb="md" gapSize="16px">
              <UtilButton
                iconLeft="chevron_left"
                size="large"
                onClick={() => {
                  if (history.length > 1) {
                    history.goBack();
                  } else {
                    history.replace('/users');
                  }
                }}
              >
                {history.length > 1 ? tGlobal('Buttons.back') : tUsersList('title')}
              </UtilButton>
              <Title title={t('title')} />
            </Box>
            <Box width="100%" boundary="shadow" mb="lg" mt="lg" pt="md" pb="md" ps="md" pe="md" jc="sb">
              <Box wrap="wrap" gap="sm">
                <Box width="50%" height="auto" flx="col">
                  <label className={styles.label}>{t('form.currentPassword')}</label>
                  <TextField
                    key="currentPassword"
                    name="currentPassword"
                    placeholder={t('form.currentPasswordPlaceholder') || ''}
                    preset="password"
                    width="100%"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      setCurrentPassword(e.target.value);
                    }}
                    value={values.currentPassword}
                    variant="outlined"
                    error={errors.currentPassword && touched.currentPassword ? errors.currentPassword : null}
                  />
                </Box>
                <Box width="50%" height="auto" flx="col">
                  <label className={styles.label}>{t('form.newPassword')}</label>
                  <TextField
                    key="newPassword"
                    name="newPassword"
                    placeholder={t('form.newPasswordPlaceholder') || ''}
                    preset="password"
                    width="100%"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      setNewPassword(e.target.value);
                    }}
                    value={values.newPassword}
                    variant="outlined"
                    error={errors.newPassword && touched.newPassword ? errors.newPassword : null}
                  />
                </Box>
                <PasswordValidationWidget password={newPassword} onValidate={setHasValidPassword} />
                {passwordError && (
                  <div className={styles.errorMSG} style={{ paddingBottom: 15, paddingTop: 0 }}>
                    {t('form.passwordError')}
                  </div>
                )}
                <Box width="100%" height="auto">
                  <Button
                    id="update-password"
                    label={t('form.changePasswordButton')}
                    type="submit"
                    color="outlined"
                    isDisabled={!isValid || !hasValidPassword}
                  />
                </Box>
              </Box>
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      newPassword: Yup.string().required(t('validation.required') || ''),
    });
  }
};

export default ChangeUserPassword;
